import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Tabs,
    Tab,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    CircularProgress,
    styled,
    tableCellClasses,
    TablePagination,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    InputAdornment,
    Autocomplete
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from "react-dropzone";
import { makeApiRequest } from '../../../utils/api';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CancelIcon from '@mui/icons-material/Cancel';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import { Visibility } from '@mui/icons-material';
import { formatDate } from '../../../helpers/timeHelper';
import { useNavigate } from 'react-router-dom';
import { Grid } from "@mui/material";
import { Search } from '@mui/icons-material';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#F1F4F9",
        // color: theme.palette.common.white,
        fontWeight: "bold",
        color: "black"
    },
}));


const ViewAssignments = () => {
    const [assignments, setAssignments] = useState([]);
    const [filteredAssignments, setFilteredAssignments] = useState([]);
    const [submitted, setSubmitted] = useState([]);
    const [statusFilter, setStatusFilter] = useState("submitted");
    const [openModal, setOpenModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [files, setFiles] = useState([]);
    const [uploadStatus, setUploadStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [courseList, setCourseList] = useState([]);
    const [error, setError] = useState();
    const [selectedCourse, setSelectedCourse] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [batchList, setBatchList] = useState([]);
    const [selectBatch, setSelectedBatch] = useState("");


    const navigate = useNavigate();

    useEffect(() => {
        const fetchAssignments = async () => {
            setLoading(true);
            try {
                const response = await makeApiRequest('post', '/api/assignments/list', {
                    token: localStorage.getItem('authToken')
                });
                setAssignments(response.data);
                setSubmitted(response.data.filter((assignment) => assignment.status === "submitted" && (assignment.stud_status || "").toLowerCase() === "active"));
                setLoading(false);
            } catch (error) {
                console.error("Error fetching assignments:", error);
                setAssignments([]);
            }
        };
        fetchAssignments();
    }, []);



    const isActiveStudent = (assignment) => (assignment.stud_status || "").toLowerCase() === "active";

    useEffect(() => {
        if (statusFilter === "all") {
            setFilteredAssignments(assignments);
        } else {
            setFilteredAssignments(assignments.filter((assignment) => assignment.status === statusFilter && isActiveStudent(assignment)));
        }
    }, [statusFilter, assignments]);

    const openReview = (assignment) => {
        navigate(`/review-assignment`, {
            state: {
                id: assignment.id,
                submittedAssignments: submitted
            }
        });
    }

    const onDrop = useCallback((acceptedFiles) => {
        setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    }, []);


    useEffect(() => {
        let filtered = assignments;

        if (statusFilter !== "all") {
            filtered = filtered.filter((assignment) => assignment.status === statusFilter && isActiveStudent(assignment));
        }

        if (selectedCourse) {
            filtered = filtered.filter((assignment) => assignment.course_id === selectedCourse);
        }
        if (selectBatch) {
            filtered = filtered.filter((assignment) => assignment.batch_id === selectBatch);
        }

        if (searchQuery) {
            filtered = filtered.filter((assignment) =>
                assignment.stud_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                assignment.project_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                assignment.stud_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                assignment.created_by.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        setFilteredAssignments(filtered);
    }, [statusFilter, selectedCourse, selectBatch, searchQuery, assignments]);

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const fetchBatchData = async () => {
        setError(null);
        if (batchList.length > 0) return;
        setError(null);
        try {
            const response = await makeApiRequest('post', '/api/listBatches/active', {
                token: localStorage.getItem('authToken'),
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });


            if (!response || response.status !== 'success' || !response.data) {
                throw new Error(response?.message || 'Invalid response from API');
            }
            const responseData = response.data;

            setBatchList(responseData);
        } catch (err) {
            console.error("Error fetching batches:", err);
            setError(err.message || 'Error fetching data');
            setBatchList([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCourseData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await makeApiRequest('post', '/api/course/list', {
                token: localStorage.getItem('authToken'),
            });
            if (!response || response.status !== 'success' || !response.data) {
                throw new Error(response?.message || 'Invalid response from API');
            }
            setCourseList(response.data);
        }
        catch (err) {
            console.error("Error fetching batches:", err);
            setError(err.message || 'Error fetching data');
            setCourseList([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchCourseData();
        fetchBatchData();
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true });
    if (loading) return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
        >
            <CircularProgress
                size={40}
                thickness={4}
                sx={{
                    color: '#481E82',
                }}
            />
        </Box>
    );


    return (
        <div style={{ minHeight: "100vh", maxWidth: '100%', margin: 'auto', padding: '24px', backgroundColor: "#F1F4F9", fontFamily: "Roboto" }}>
            <Box sx={{ height: "50px", display: "flex", alignItems: "center" }}>
                <Typography variant='h4' sx={{ color: "#481E82", fontWeight: "500", fontSize: { xs: '24px', sm: '34px' } }}>  View Assignments</Typography>
            </Box>
            <Tabs value={statusFilter} /* sx={{ py: 2 }} */ onChange={(e, newValue) => setStatusFilter(newValue)}
                sx={{ py: 2, minHeight: 40, "& .MuiTab-root": { minHeight: 32, fontSize: "0.875rem" } }}
            >
                <Tab icon={<BorderAllIcon />} iconPosition="start" label={`All (${assignments.length})`} value="all" />
                <Tab icon={<PeopleAltIcon />} iconPosition="start" label={`Pending With Staff (${assignments.filter(a => a.status === "submitted" && isActiveStudent(a)).length})`} value="submitted" />
                <Tab icon={<SchoolIcon />} iconPosition="start" label={`Pending With Student (${assignments.filter(a => a.status === "assigned" && isActiveStudent(a)).length})`} value="assigned" />
                <Tab icon={<CancelIcon />} iconPosition="start" label={`Rejected (${assignments.filter(a => a.status === "rejected" && isActiveStudent(a)).length})`} value="rejected" />
                <Tab icon={<CheckCircleIcon />} iconPosition="start" label={`Completed (${assignments.filter(a => a.status === "reviewed" && isActiveStudent(a)).length})`} value="reviewed" />
            </Tabs>

            <Box sx={{ backgroundColor: "white", p: 2, mt: 2, borderRadius: "5px 5px 0 0" }} >
                <Grid container spacing={2} sx={{ justifyContent: "space-between" }}>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Autocomplete
                            size="small"
                            fullWidth
                            options={batchList}
                            getOptionLabel={(option) => option.batch_name || ""}
                            value={batchList.find((c) => c.id === selectBatch) || null}
                            onChange={(event, newValue) => setSelectedBatch(newValue ? newValue.id : "")}
                            renderInput={(params) => (
                                <TextField {...params} label="Batch" fullWidth />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Autocomplete
                            size="small"
                            fullWidth
                            options={courseList}
                            getOptionLabel={(option) => option.course_name || ""}
                            value={courseList.find((c) => c.id === selectedCourse) || null}
                            onChange={(event, newValue) => setSelectedCourse(newValue ? newValue.id : "")}
                            renderInput={(params) => (
                                <TextField {...params} label="Course" fullWidth />
                            )}
                        />
                    </Grid>
                </Grid>

            </Box>


            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>S.NO</StyledTableCell>
                            <StyledTableCell>Student Name</StyledTableCell>
                            <StyledTableCell>Project Title</StyledTableCell>
                            <StyledTableCell>Assigned By</StyledTableCell>
                            <StyledTableCell>Batch & Course</StyledTableCell>
                            <StyledTableCell>Module & Chapter</StyledTableCell>
                            <StyledTableCell>Action</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredAssignments
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((assignment, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{assignment.stud_name}<br /><Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>{assignment.stud_id}</Typography></TableCell>
                                    <TableCell>{assignment.project_title}</TableCell>
                                    <TableCell>{assignment.created_by}<br /><Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>{formatDate(assignment.assign_date)}</Typography></TableCell>
                                    <TableCell>{assignment.batch_name}<br /><Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>{assignment.course_name}</Typography></TableCell>
                                    <TableCell>{assignment.module_name}<br /><Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>{formatDate(assignment.chapter_name)}</Typography></TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => openReview(assignment)} sx={{ color: "#1976D2" }}>
                                            <Visibility />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}

                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={filteredAssignments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </div>
    );
};

export default ViewAssignments;
